import { formatNumber } from '@if/core/utils'
import { Banner, Caption, FieldLabel, Icon, StepLinearProgress, StrongValue } from '@if/mui'
import { Box, Card, CardContent, CardHeader, Stack } from '@mui/material'
import { Filter } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { MetricTotals } from '../statistics-hooks'
import { PerformanceTab, PERFORMANCE_TABS } from '../statistics-metrics'

interface ConversionFunnelProps {
  totals: MetricTotals
}

interface FunnelRowProps {
  label: string
  value: number
  percentOfTotal: number
  tone: PerformanceTab['tone']
  caption?: string
}

const FunnelRow = ({ label, value, percentOfTotal, tone, caption }: FunnelRowProps) => (
  <div>
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <FieldLabel>{label}</FieldLabel>
      <StrongValue>{formatNumber(value)}</StrongValue>
    </Stack>
    <Box sx={{ my: 0.5 }}>
      <StepLinearProgress steps={[{ from: 0, color: tone }]} value={Math.min(percentOfTotal, 100)} max={100} />
    </Box>
    {caption && <Caption>{caption}</Caption>}
  </div>
)

export const ConversionFunnel = ({ totals }: ConversionFunnelProps) => {
  const { t } = useTranslation()

  const applications = totals.totalApplications
  const approved = totals.totalApprovedApplications
  const loans = totals.totalCreatedLoans

  const approvalRate = applications > 0 ? Math.round((approved / applications) * 100) : 0
  const fundedRate = approved > 0 ? Math.round((loans / approved) * 100) : 0
  const notYetFunded = Math.max(approved - loans, 0)

  return (
    <Card>
      <CardHeader avatar={<Icon icon={Filter} size="sm" />} title={t('merchantStatistics.performance.funnel.title')} />
      <CardContent>
        <Stack spacing={3}>
          <FunnelRow
            label={t('merchantStatistics.performance.funnel.applications')}
            value={applications}
            percentOfTotal={100}
            tone={PERFORMANCE_TABS[0].tone}
          />
          <FunnelRow
            label={t('merchantStatistics.performance.funnel.approved')}
            value={approved}
            percentOfTotal={applications > 0 ? (approved / applications) * 100 : 0}
            caption={t('merchantStatistics.performance.funnel.approvalRateCaption', { rate: approvalRate })}
            tone={PERFORMANCE_TABS[1].tone}
          />
          <FunnelRow
            label={t('merchantStatistics.performance.funnel.loansCreated')}
            value={loans}
            percentOfTotal={applications > 0 ? (loans / applications) * 100 : 0}
            caption={t('merchantStatistics.performance.funnel.fundedRateCaption', { rate: fundedRate })}
            tone={PERFORMANCE_TABS[2].tone}
          />
        </Stack>

        {notYetFunded > 0 && (
          <Banner severity="warning" sx={{ mt: 3 }}>
            {t('merchantStatistics.performance.funnel.opportunity', { count: notYetFunded })}
          </Banner>
        )}
      </CardContent>
    </Card>
  )
}
